import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Terminal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { buildPartnerCurl, PARTNER_API_ENDPOINTS, type CurlAction } from "@/lib/partner-api-curl";
import { slugifyCustomFieldKey } from "@/lib/partner-api";
import {
  getMessagingIntegrationDetail,
  getMessagingIntegrationLabel,
  type MessagingIntegrationOption,
} from "@/lib/integration-display-label";
import { resolveWorkspaceMemberLabel } from "@/lib/workspace-member-display";
import {
  PartnerApiIdCatalog,
  type CatalogFunnel,
  type CatalogMember,
  type CatalogStage,
  type CatalogTag,
} from "@/components/settings/PartnerApiIdCatalog";
import { PartnerApiCurlCustomFields, type CustomFieldRow } from "@/components/settings/PartnerApiCurlCustomFields";

const ACTIONS: { value: CurlAction; label: string }[] = [
  { value: "create_lead", label: "Enviar / atualizar lead" },
  { value: "get_lead", label: "Buscar um lead (com os campos dele)" },
  { value: "list_leads", label: "Listar leads" },
  { value: "get_funnels", label: "GET funis (só se o código precisar consultar em runtime)" },
];

const STEPS = [
  {
    title: "Crie uma API Key",
    body: "Na aba API Keys: leads:write + funnels:read para enviar leads (custom_fields entra no mesmo POST, sem scope extra). Copie a chave (fab_…) na hora.",
  },
  {
    title: "Use os IDs listados abaixo",
    body: "Funil e responsável são UUID. Coluna é o slug. Tags aceitam UUID ou o nome. custom_fields é um objeto livre deste lead: invente chave e valor no POST, sem cadastrar schema.",
  },
  {
    title: "Preencha o lead",
    body: "Obrigatório: name. Recomendado: phone (ex. 11999999999). funnel_id + stage colocam o card no kanban. external_id evita duplicar o cadastro do seu sistema.",
  },
  {
    title: "Opcional: webhook",
    body: "Cadastre uma URL HTTPS na aba Webhooks. A assinatura vem em X-Webhook-Signature: sha256=HMAC(secret, timestamp + \".\" + body).",
  },
] as const;

const FIELD_HELP: [string, string][] = [
  ["name", "Obrigatório. Nome no CRM."],
  ["phone", "Com DDD. Mesmo telefone atualiza o lead (upsert)."],
  ["funnel_id", "UUID da lista abaixo. Alternativa: funnel com o nome do funil."],
  ["stage / column", "Slug da lista (novo) ou o nome visível da coluna."],
  ["external_id", "ID no seu ERP. Também faz upsert."],
  ["custom_fields", "Objeto livre deste lead. Cada chave é um slug (a-z, 0-9, _). Crie e preencha no POST/PUT — nada é obrigatório e não cadastra schema. Volta em GET /v1/leads e GET /v1/leads/:id e aparece no perfil do CRM."],
  ["tag_ids ou tags", "UUIDs da lista ou os nomes das tags."],
  ["assigned_member_id", "UUID do membro na lista. Sem isso o lead entra sem responsável."],
  ["Idempotency-Key", "Header opcional. Se o mesmo envio falhar na rede e for repetido com a mesma chave e o mesmo JSON, a API devolve o resultado anterior em vez de criar outro lead. Use um valor único por envio (ex.: form-001, pedido-48291)."],
  ["send_greeting", "Opcional. Envia a mensagem de saudação do agent_id pelo WhatsApp. Conta como mensagem de IA — o agente continua ativo. Exige phone, agent_id e conexão WhatsApp compatível."],
  ["agent_id", "UUID do agente cuja greeting_message será enviada (obrigatório com send_greeting)."],
  ["integration_id", "UUID da conexão WhatsApp. Se houver só uma ativa, pode omitir."],
];

export function PartnerApiReference({
  workspaceId,
  baseUrl,
}: {
  workspaceId: string;
  baseUrl: string;
}) {
  const { toast } = useToast();
  const [funnels, setFunnels] = useState<CatalogFunnel[]>([]);
  const [allStages, setAllStages] = useState<CatalogStage[]>([]);
  const [tags, setTags] = useState<CatalogTag[]>([]);
  const [members, setMembers] = useState<CatalogMember[]>([]);
  const [apiKey, setApiKey] = useState("");
  const [action, setAction] = useState<CurlAction>("create_lead");
  const [name, setName] = useState("Maria Silva");
  const [phone, setPhone] = useState("11999999999");
  const [email, setEmail] = useState("");
  const [funnelId, setFunnelId] = useState("");
  const [stage, setStage] = useState("");
  const [externalId, setExternalId] = useState("");
  const [leadId, setLeadId] = useState("");
  const [customRows, setCustomRows] = useState<CustomFieldRow[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [assignedMemberId, setAssignedMemberId] = useState("");
  const [includeIdempotency, setIncludeIdempotency] = useState(true);
  const [sendGreeting, setSendGreeting] = useState(false);
  const [greetingAgentId, setGreetingAgentId] = useState("");
  const [greetingIntegrationId, setGreetingIntegrationId] = useState("");
  const [agents, setAgents] = useState<Array<{ id: string; name: string; display_name: string | null; greeting_message: string | null }>>([]);
  const [uazapiIntegrations, setUazapiIntegrations] = useState<Array<{ id: string; label: string }>>([]);

  const stages = useMemo(
    () => allStages.filter((s) => s.funnel_id === funnelId),
    [allStages, funnelId],
  );

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copiado" });
  };

  const greetingAgents = useMemo(
    () => agents.filter((a) => (a.greeting_message || "").trim().length > 0),
    [agents],
  );

  const load = useCallback(async () => {
    const [funnelRes, stageRes, tagRes, memberRes, agentRes, integrationRes] = await Promise.all([
      supabase.from("sales_funnels").select("id, name, is_default").eq("workspace_id", workspaceId).order("order_index"),
      supabase.from("lead_stages").select("funnel_id, slug, name").eq("workspace_id", workspaceId).order("order_index"),
      supabase.from("lead_tags").select("id, name").eq("workspace_id", workspaceId).order("name"),
      supabase.from("workspace_members").select("id, display_name, profile:profiles(full_name)").eq("workspace_id", workspaceId),
      supabase.from("agents").select("id, name, display_name, greeting_message, is_active").eq("workspace_id", workspaceId).eq("is_active", true).order("name"),
      supabase.from("integrations").select("id, provider, config, instance_identifier").eq("workspace_id", workspaceId).eq("provider", "whatsapp_uazapi").eq("status", "active").order("created_at"),
    ]);
    const funnelRows = (funnelRes.data || []) as CatalogFunnel[];
    setFunnels(funnelRows);
    setAllStages((stageRes.data || []) as CatalogStage[]);
    setTags((tagRes.data || []) as CatalogTag[]);
    setMembers(
      ((memberRes.data || []) as unknown as { id: string; display_name: string | null; profile?: { full_name: string | null } | null }[]).map((m) => ({
        id: m.id,
        name: resolveWorkspaceMemberLabel(m.display_name, m.profile?.full_name),
      })),
    );
    const agentRows = (agentRes.data || []) as Array<{ id: string; name: string; display_name: string | null; greeting_message: string | null }>;
    setAgents(agentRows);
    const integrationRows = (integrationRes.data || []) as Array<{
      id: string;
      config?: Record<string, unknown> | null;
      instance_identifier?: string | null;
    }>;
    setUazapiIntegrations(
      integrationRows.map((row) => {
        const opt: MessagingIntegrationOption = {
          id: row.id,
          provider: "whatsapp_uazapi",
          config: row.config ?? null,
          instance_identifier: row.instance_identifier ?? null,
          instagram_account_id: null,
          page_id: null,
          status: "active",
        };
        const name = getMessagingIntegrationLabel(opt);
        const phone = getMessagingIntegrationDetail(opt).trim();
        const label = phone ? `${name} · ${phone}` : name;
        return { id: row.id, label };
      }),
    );
    const initialFunnel = funnelRows.find((f) => f.is_default) || funnelRows[0];
    if (initialFunnel) setFunnelId((prev) => prev || initialFunnel.id);
    const initialGreetingAgent = agentRows.find((a) => (a.greeting_message || "").trim());
    if (initialGreetingAgent) setGreetingAgentId((prev) => prev || initialGreetingAgent.id);
    if (integrationRows.length === 1) {
      setGreetingIntegrationId((prev) => prev || integrationRows[0].id);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setStage((prev) => (prev && stages.some((r) => r.slug === prev) ? prev : stages[0]?.slug || ""));
  }, [funnelId, stages]);

  const customFields = useMemo(() => {
    const out: Record<string, string> = {};
    for (const row of customRows) {
      const key = slugifyCustomFieldKey(row.key);
      if (key && row.value.trim()) out[key] = row.value.trim();
    }
    return out;
  }, [customRows]);

  const curl = useMemo(
    () => buildPartnerCurl({
      baseUrl,
      apiKey,
      action,
      name,
      phone,
      email,
      funnelId,
      stage,
      externalId,
      customFields,
      tagIds,
      assignedMemberId,
      includeIdempotency,
      sendGreeting,
      agentId: greetingAgentId,
      integrationId: greetingIntegrationId,
      leadId,
    }),
    [baseUrl, apiKey, action, name, phone, email, funnelId, stage, externalId, customFields, tagIds, assignedMemberId, includeIdempotency, sendGreeting, greetingAgentId, greetingIntegrationId, leadId],
  );

  return (
    <div className="space-y-8">
      <header className="space-y-2 max-w-[70ch]">
        <h3 className="text-xl font-semibold tracking-tight text-foreground text-balance">
          Como enviar um lead
        </h3>
        <p className="text-sm leading-relaxed text-muted-foreground text-pretty">
          Monte o curl com os IDs deste workspace e cole no seu formulário, ERP ou Postman.
        </p>
        <div className="flex items-center gap-2 pt-1">
          <code className="text-[12px] font-mono truncate rounded-md border bg-muted/50 px-2 py-1.5 flex-1 text-foreground">{baseUrl}</code>
          <Button type="button" size="sm" variant="outline" onClick={() => copy(baseUrl)}>
            <Copy className="h-3.5 w-3.5 mr-1.5" />
            URL
          </Button>
        </div>
      </header>

      <ol className="space-y-4 max-w-[70ch]">
        {STEPS.map((step, i) => (
          <li key={step.title} className="flex gap-3">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-[13px] font-semibold text-primary-foreground">
              {i + 1}
            </span>
            <div className="space-y-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{step.title}</p>
              <p className="text-sm leading-relaxed text-muted-foreground">{step.body}</p>
            </div>
          </li>
        ))}
      </ol>

      <PartnerApiIdCatalog
        data={{ workspaceId, funnels, stages: allStages, tags, members }}
        onCopy={copy}
      />

      <section className="space-y-4 rounded-lg border border-border bg-background p-4 sm:p-5">
        <div className="flex items-start gap-2">
          <Terminal className="h-4 w-4 mt-0.5 text-primary shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-foreground">Gerador de curl</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              A chave fica só no navegador. Campos extras deste lead vão em custom_fields no POST e voltam iguais no GET.
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="partner-api-key">API Key</Label>
            <Input
              id="partner-api-key"
              type="password"
              autoComplete="off"
              placeholder="fab_…"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>O que você quer fazer</Label>
            <Select value={action} onValueChange={(v) => setAction(v as CurlAction)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {action === "get_lead" && (
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="partner-lead-id">ID do lead</Label>
              <Input id="partner-lead-id" value={leadId} onChange={(e) => setLeadId(e.target.value)} placeholder="uuid do lead" />
              <p className="text-sm text-muted-foreground">
                A resposta inclui <span className="font-mono text-[12px] text-foreground">custom_fields</span> gravados neste lead.
              </p>
            </div>
          )}

          {action === "create_lead" && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="partner-lead-name">Nome</Label>
                <Input id="partner-lead-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="partner-lead-phone">Telefone</Label>
                <Input id="partner-lead-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="11999999999" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="partner-lead-email">E-mail</Label>
                <Input id="partner-lead-email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="partner-lead-ext">ID externo</Label>
                <Input id="partner-lead-ext" value={externalId} onChange={(e) => setExternalId(e.target.value)} placeholder="crm-123" />
              </div>
              <div className="space-y-1.5">
                <Label>Funil</Label>
                <Select value={funnelId || undefined} onValueChange={setFunnelId}>
                  <SelectTrigger><SelectValue placeholder="Selecione o funil" /></SelectTrigger>
                  <SelectContent>
                    {funnels.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Coluna</Label>
                <Select value={stage || undefined} onValueChange={setStage}>
                  <SelectTrigger><SelectValue placeholder="Selecione a coluna" /></SelectTrigger>
                  <SelectContent>
                    {stages.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name} ({s.slug})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Responsável</Label>
                <Select value={assignedMemberId || "__none"} onValueChange={(v) => setAssignedMemberId(v === "__none" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Nenhum" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Nenhum</SelectItem>
                    {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {tags.length > 0 && (
                <div className="sm:col-span-2 space-y-2">
                  <Label>Tags</Label>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {tags.map((tag) => (
                      <label key={tag.id} className="flex items-center gap-2 text-sm text-foreground">
                        <Checkbox
                          checked={tagIds.includes(tag.id)}
                          onCheckedChange={(v) => setTagIds((prev) => (v === true ? [...prev, tag.id] : prev.filter((id) => id !== tag.id)))}
                        />
                        {tag.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <PartnerApiCurlCustomFields rows={customRows} onChange={setCustomRows} />
              <div className="sm:col-span-2 space-y-3 rounded-md border border-border/80 bg-muted/20 p-3">
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <Checkbox
                    checked={sendGreeting}
                    onCheckedChange={(v) => setSendGreeting(v === true)}
                    disabled={greetingAgents.length === 0 || uazapiIntegrations.length === 0}
                  />
                  Enviar saudação do agente
                </label>
                <p className="text-sm text-muted-foreground leading-relaxed pl-6">
                  Ao criar o lead, envia a <span className="font-mono text-[12px] text-foreground">greeting_message</span> do agente
                  sem usar IA/LLM. Conta como mensagem de IA — o atendimento automático continua quando o lead responder.
                  Exige uma conexão WhatsApp que permita envio proativo ao contato.
                </p>
                {sendGreeting && (
                  <div className="grid gap-3 sm:grid-cols-2 pl-6">
                    <div className="space-y-1.5">
                      <Label>Agente</Label>
                      <Select value={greetingAgentId || undefined} onValueChange={setGreetingAgentId}>
                        <SelectTrigger><SelectValue placeholder="Selecione o agente" /></SelectTrigger>
                        <SelectContent>
                          {greetingAgents.map((a) => (
                            <SelectItem key={a.id} value={a.id}>
                              {(a.display_name || a.name).trim()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Conexão WhatsApp</Label>
                      <Select
                        value={greetingIntegrationId || undefined}
                        onValueChange={setGreetingIntegrationId}
                        disabled={uazapiIntegrations.length <= 1}
                      >
                        <SelectTrigger><SelectValue placeholder="Selecione a conexão" /></SelectTrigger>
                        <SelectContent>
                          {uazapiIntegrations.map((i) => (
                            <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {uazapiIntegrations.length > 1 && (
                        <p className="text-xs text-muted-foreground">Obrigatório no JSON quando há mais de uma conexão ativa.</p>
                      )}
                    </div>
                  </div>
                )}
                {greetingAgents.length === 0 && (
                  <p className="text-sm text-muted-foreground italic pl-6">Nenhum agente ativo com mensagem de saudação configurada.</p>
                )}
                {uazapiIntegrations.length === 0 && (
                  <p className="text-sm text-muted-foreground italic pl-6">Nenhuma conexão WhatsApp ativa para saudação automática neste workspace.</p>
                )}
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <Checkbox checked={includeIdempotency} onCheckedChange={(v) => setIncludeIdempotency(v === true)} />
                  Incluir Idempotency-Key
                </label>
                <p className="text-sm text-muted-foreground leading-relaxed pl-6">
                  Evita lead duplicado se o formulário ou integração reenviar o mesmo POST por timeout, clique duplo ou retry automático.
                  Com a mesma chave e o mesmo corpo JSON, a FabChat responde com o lead já criado — não cria outro.
                  Troque o valor a cada envio novo (ex.: <span className="font-mono text-[12px] text-foreground">form-001</span>,{" "}
                  <span className="font-mono text-[12px] text-foreground">pedido-48291</span>).
                </p>
              </div>
            </>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label>Comando pronto</Label>
            <Button type="button" size="sm" onClick={() => copy(curl)}>
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              Copiar curl
            </Button>
          </div>
          <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-[12px] leading-relaxed text-foreground font-mono whitespace-pre-wrap break-all">
            {curl}
          </pre>
        </div>
      </section>

      <section className="space-y-3 max-w-[70ch]">
        <h4 className="text-sm font-semibold text-foreground">Campos do POST /v1/leads</h4>
        <dl className="space-y-2 text-sm">
          {FIELD_HELP.map(([term, def]) => (
            <div key={term} className="grid gap-0.5 sm:grid-cols-[11rem_1fr] sm:gap-3">
              <dt className="font-mono text-[12px] text-foreground">{term}</dt>
              <dd className="text-muted-foreground leading-relaxed">{def}</dd>
            </div>
          ))}
        </dl>
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Exemplo de custom_fields</p>
          <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-[12px] leading-relaxed text-foreground font-mono whitespace-pre">{`{
  "name": "Maria Silva",
  "phone": "11999999999",
  "custom_fields": {
    "placa": "ABC1D23",
    "utm_source": "google"
  }
}`}</pre>
          <p className="text-sm text-muted-foreground leading-relaxed">
            GET /v1/leads/:id devolve o mesmo objeto em <span className="font-mono text-[12px] text-foreground">data.custom_fields</span>. PUT mescla as chaves enviadas; valor vazio remove a chave. Limite: 50 campos por lead.
          </p>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          60 req/min por chave. Autenticação só em X-Api-Key. GET de funis/tags continua existindo para o seu código consultar sozinho depois.
        </p>
      </section>

      <section className="space-y-2 max-w-[70ch]">
        <h4 className="text-sm font-semibold text-foreground">Endpoints</h4>
        <ul className="space-y-1.5 text-sm">
          {PARTNER_API_ENDPOINTS.map(([method, path, hint]) => (
            <li key={`${method}${path}`} className="grid gap-0.5 sm:grid-cols-[4.5rem_1fr] sm:gap-3">
              <span className="font-mono text-[11px] font-medium text-primary">{method}</span>
              <span>
                <span className="font-mono text-[12px] text-foreground">{path}</span>
                <span className="text-muted-foreground"> — {hint}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
